"use client";

import { RefObject, useEffect } from "react";

/** How far into the target phrase the arrow's tip lands, as a share of its width. */
const TIP_INTO_TARGET = 0.3;

/** The arrow's tip sits this share of its drawn width in from the note's left edge. */
const TIP_IN_ARROW = 13 / 66;

/** Room the tilted note keeps inside the column's right edge, so it never runs into the gutter. */
const COLUMN_CLEARANCE_PX = 8;

/** Above this width the note may break into two lines to reach its target. */
const TWO_LINE_QUERY = "(width > 900px)";

/**
 * Places the pen note so its arrow's tip lands a third of the way into the
 * phrase it annotates, wherever the headline wraps. If the one-line note is
 * too wide for the tip to reach, it breaks into two lines (`data-two-line`)
 * and is placed again. Runs on resize and once the fonts have loaded, since
 * both move the phrase. Writes the margin to the DOM directly: a measurement
 * loop has no business re-rendering the hero.
 */
export function usePenPlacement(
  wrapRef: RefObject<HTMLElement | null>,
  targetRef: RefObject<HTMLElement | null>,
  penRef: RefObject<HTMLElement | null>,
  arrowRef: RefObject<SVGSVGElement | null>,
) {
  useEffect(() => {
    const wrap = wrapRef.current;
    const target = targetRef.current;
    const pen = penRef.current;
    const arrow = arrowRef.current;

    if (!wrap || !target || !pen || !arrow) return;

    const twoLineQuery = window.matchMedia(TWO_LINE_QUERY);

    const place = () => {
      pen.style.marginLeft = "";
      delete pen.dataset.twoLine;

      const wrapBox = wrap.getBoundingClientRect();
      const targetBox = target.getBoundingClientRect();
      const tipOffset = arrow.getBoundingClientRect().width * TIP_IN_ARROW;
      const targetStart = targetBox.left - wrapBox.left;
      const idealLeft = targetStart + targetBox.width * TIP_INTO_TARGET - tipOffset;
      let maxLeft = wrapBox.width - pen.offsetWidth - COLUMN_CLEARANCE_PX;

      if (twoLineQuery.matches && maxLeft + tipOffset < targetStart) {
        pen.dataset.twoLine = "";
        maxLeft = wrapBox.width - pen.offsetWidth - COLUMN_CLEARANCE_PX;
      }

      pen.style.marginLeft = `${Math.max(0, Math.round(Math.min(idealLeft, maxLeft)))}px`;
    };

    place();

    // Width only: the note's own two-line break changes the wrap's height,
    // and reacting to that would place it twice for nothing.
    let lastWidth = wrap.getBoundingClientRect().width;
    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width === lastWidth) return;

      lastWidth = entry.contentRect.width;
      place();
    });

    resizeObserver.observe(wrap);

    let isActive = true;
    document.fonts.ready.then(() => {
      if (isActive) place();
    });

    return () => {
      isActive = false;
      resizeObserver.disconnect();
    };
  }, [wrapRef, targetRef, penRef, arrowRef]);
}
